import React from 'react';
import cn from '@/utils/cn';

// AIDEV-NOTE: Shared SPA radio, the round sibling of SPACheckbox. A real <input type="radio">
// stays in the DOM (visually hidden) so native form semantics, grouping by `name`, and keyboard
// behaviour all work; the visible circle is driven by the peer-* variants.
const circleClass = [
  'tw-relative tw-h-4 tw-w-4 tw-shrink-0 tw-box-border tw-rounded-full',
  'tw-border tw-border-solid tw-border-spa-border-input tw-bg-spa-bg',
  'tw-flex tw-items-center tw-justify-center tw-transition-colors',
  'peer-hover:tw-border-spa-accent',
  'peer-checked:tw-border-spa-accent-solid',
  'peer-focus-visible:tw-outline peer-focus-visible:tw-outline-2',
  'peer-focus-visible:tw-outline-spa-accent peer-focus-visible:tw-outline-offset-2',
  'peer-disabled:tw-bg-spa-bg-disabled peer-disabled:tw-border-spa-bg-disabled',
  '[&>span]:tw-opacity-0 peer-checked:[&>span]:tw-opacity-100',
].join(' ');

interface SPARadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  labelClassName?: string;
}

const SPARadio = React.forwardRef<HTMLInputElement, SPARadioProps>(
  ({ children, className, labelClassName, ...props }, ref) => (
    <label
      className={cn(
        // tw-relative anchors the sr-only input to this row — see SPACheckbox for the
        // focus-scroll failure mode it prevents.
        'tw-relative tw-inline-flex tw-items-center tw-gap-2 tw-m-0 tw-cursor-pointer',
        props.disabled && 'tw-cursor-not-allowed',
        className,
      )}
    >
      <input
        ref={ref}
        className="tw-peer tw-sr-only"
        type="radio"
        {...props}
      />
      <span className={circleClass}>
        <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-spa-accent-solid tw-transition-opacity" />
      </span>
      {children != null && (
        <span
          className={cn(
            'tw-text-sm tw-leading-5 tw-text-spa-primary peer-disabled:tw-text-spa-disabled',
            labelClassName,
          )}
        >
          {children}
        </span>
      )}
    </label>
  ),
);

SPARadio.displayName = 'SPARadio';

export default SPARadio;
